import {Options} from '@core/helpers/persistOptionsHelper';

interface FieldSearchOptions {
  like?: string;
  options?: string;
}

export interface ServiceFilter {
  limit?: number;
  skip?: number;
  order?: string | string[];
  include?: string[] | {relation?: string}[];
  fields?: string[];
  where?: {
    or?: {
      statusCode?: FieldSearchOptions;
      path?: FieldSearchOptions;
      verb?: FieldSearchOptions;
      email?: FieldSearchOptions;
      name?: FieldSearchOptions;
      description?: FieldSearchOptions;
      comment?: FieldSearchOptions;
      methode?: FieldSearchOptions;
      statusDescription?: FieldSearchOptions;
      URI?: FieldSearchOptions;
      companyName?: FieldSearchOptions;
      alternativeCustomerNumber?: FieldSearchOptions;
      cocNumber?: FieldSearchOptions;
      contactPerson?: FieldSearchOptions;
      emailAddress?: FieldSearchOptions;
      salesEmailAddress?: FieldSearchOptions;
      phoneNumber?: FieldSearchOptions;
      salesPhoneNumber?: FieldSearchOptions;
    }[];
    and?: {
      isFunctional?: boolean;
      status?: {eq?: string};
      portfolioReleaseId?: string;
      AdaStatus?: {lt?: number; gte?: number};
      solutionId?: boolean | number | null;
      active?: boolean;
    }[];
    status?: {eq?: string};
    prospectId?: number;
    solutionId?: number;
    validatedObjectId?: number;
    statusCode?: {eq?: number};
    domainId?: number;
    releaseDate?: string;
    portfolioReleaseId?: string;
    AdaStatus?: {lte?: number};
  };
}

export function setApplicationOptions(
  options: Options,
  search?: string,
): string {
  const params = new URLSearchParams();

  if (options.page && options.itemsPerPage) {
    params.append('page', String(options.page));
    params.append('itemsPerPage', String(options.itemsPerPage));
  }

  if (search) {
    params.append('search', search);
  }

  if (options?.sortBy && options.sortBy.length > 0) {
    const order = options.sortBy.map(
      item =>
        `${item.key} ${typeof item.order === 'string' ? item.order?.toUpperCase() : item.order}`,
    );
    params.append('order', order.join(','));
  }

  if (options?.filter?.ids) {
    for (const id of options.filter.ids) {
      params.append('ids[]', id);
    }
  }

  return '?' + params.toString();
}

export function setLimitAndSkipFilterOptions(
  filter: ServiceFilter,
  options: Options,
): ServiceFilter {
  if (options?.itemsPerPage && options.itemsPerPage !== -1 && options.page) {
    filter.limit = options.itemsPerPage;
    filter.skip = options.itemsPerPage * (options.page - 1);
  }
  if (options?.sortBy && options.sortBy.length > 0) {
    filter.order = options.sortBy.map(
      item =>
        `${item.key} ${typeof item.order === 'string' ? item.order?.toUpperCase() : item.order}`,
    );
  }
  return filter;
}

export function setNameSearchFilterOptionsLogging(
  filter: ServiceFilter,
  options: Options,
): ServiceFilter {
  if (options?.filter?.nameSearch) {
    const p = `${options.filter.nameSearch}`;
    filter.where = {
      or: [
        {statusCode: {like: p, options: 'i'}},
        {path: {like: p, options: 'i'}},
        {verb: {like: p, options: 'i'}},
        {email: {like: p, options: 'i'}},
      ],
    };
  }
  return filter;
}

export function setNameSearchFilterOptions(
  filter: ServiceFilter,
  options: Options,
): ServiceFilter {
  if (options?.filter?.nameSearch) {
    const p = `%${options.filter.nameSearch}%`;
    filter.where = {
      or: [
        {name: {like: p}},
        {description: {like: p}},
        {comment: {like: p}},
        {methode: {like: p}},
        {statusDescription: {like: p}},
        {URI: {like: p}},
      ],
    };
    filter.include = [{relation: 'terms'}];
    filter.order = 'name ASC';
  }
  return filter;
}

export function addActiveCompaniesFilter(
  filter: ServiceFilter,
  options: Options,
): ServiceFilter {
  if (options?.filter?.activeCompany === true) {
    const statusObject = {status: {eq: 'ACTIVE'}};
    if (filter.where) {
      if (filter.where.and) {
        filter.where.and.push(statusObject);
      } else {
        filter.where = {
          and: [
            {
              ...filter.where,
              AdaStatus: filter.where?.AdaStatus?.lte
                ? {lt: filter.where.AdaStatus.lte}
                : undefined,
            },
            statusObject,
          ],
        };
      }
    } else {
      filter.where = statusObject;
    }
  }
  return filter;
}

export function addNameSearchCompanies(
  filter: ServiceFilter,
  options: Options,
): ServiceFilter {
  if (options?.filter?.nameSearch) {
    const p = `%${options.filter.nameSearch}%`;
    filter.where = {
      or: [
        {companyName: {like: p, options: 'i'}},
        {alternativeCustomerNumber: {like: p, options: 'i'}},
        {cocNumber: {like: p, options: 'i'}},
        {contactPerson: {like: p, options: 'i'}},
        {emailAddress: {like: p, options: 'i'}},
        {salesEmailAddress: {like: p, options: 'i'}},
        {phoneNumber: {like: p, options: 'i'}},
        {salesPhoneNumber: {like: p, options: 'i'}},
      ],
    };
  }
  return filter;
}
